/**
 * ShippingLineSelect - 船公司选择器
 *
 * 下拉列表来自 `constants/shippingLines.ts` 的规范 SCAC 表，但**不限于**该表：
 * 列表里没有的船公司，直接在搜索框里输入即可作为自定义值录入（存原始大写值，
 * 后端 `normalizeScac()` 对未知值本来就原样透传，不会丢数据）。
 *
 * 搜索同时匹配全名、短名和所有别名 —— 输入 "MSK" 也能找到 Maersk。
 */

import React, { useMemo, useState } from 'react';
import { Select } from 'antd';
import { SHIPPING_LINES, normalizeScac } from '../../constants/shippingLines';

interface LineOption {
  value: string;
  label: string;
  /** 大写后的可搜索文本（全名 + 短名 + 全部别名） */
  keywords: string;
}

const BASE_OPTIONS: LineOption[] = SHIPPING_LINES.map(line => ({
  value: line.code,
  label: `${line.fullName} (${line.code})`,
  keywords: [line.code, line.fullName, line.short, ...line.aliases].join(' ').toUpperCase(),
}));

const KNOWN_CODES = new Set(SHIPPING_LINES.map(line => line.code));

export interface ShippingLineSelectProps {
  value?: string | null;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  style?: React.CSSProperties;
  className?: string;
  size?: 'small' | 'middle' | 'large';
  getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
}

const ShippingLineSelect: React.FC<ShippingLineSelectProps> = ({
  value,
  onChange,
  placeholder = 'Select or type steamship line',
  disabled,
  allowClear = true,
  style,
  className,
  size,
  getPopupContainer,
}) => {
  const [query, setQuery] = useState('');

  const normalized = normalizeScac(value ?? null) || undefined;

  const options = useMemo(() => {
    const list = [...BASE_OPTIONS];

    // 已存的非标准值（历史数据或之前手输的）也要能正常回显、不被清掉
    if (normalized && !KNOWN_CODES.has(normalized)) {
      list.unshift({ value: normalized, label: `${normalized} (custom)`, keywords: normalized });
    }

    // 输入的内容不是任何已知代码/别名时，给一个"直接录入"的选项
    const q = query.trim().toUpperCase();
    if (q && !KNOWN_CODES.has(normalizeScac(q) || '') && q !== normalized) {
      list.unshift({ value: q, label: `Use "${q}"`, keywords: q });
    }

    return list;
  }, [normalized, query]);

  const handleChange = (next?: string) => {
    setQuery('');
    onChange(next ? normalizeScac(next) || undefined : undefined);
  };

  return (
    <Select
      value={normalized}
      onChange={handleChange}
      onSearch={setQuery}
      onBlur={() => setQuery('')}
      placeholder={placeholder}
      disabled={disabled}
      allowClear={allowClear}
      showSearch
      size={size}
      style={style}
      className={className}
      getPopupContainer={getPopupContainer}
      options={options}
      filterOption={(input, option) =>
        !!option && option.keywords.includes(input.trim().toUpperCase())
      }
    />
  );
};

export default ShippingLineSelect;
